import {
    AppDispatch,
    ImageProcessingState,
    ImageWarnings,
    JobStatusType,
    maxIconImagesCount,
    PreviouslyParsedTagValues,
} from "@nova/constants"
import { IconImage } from "@nova/protobuf"
import { IngestDataType } from "@nova/schemas"
import { dateAsNumber } from "@nova/serializer/src/client/util/index"
import {
    compareFractionalIndices,
    createFractionalIndexBetween,
    END,
    START,
} from "@nova/serializer/src/client/util/fractional_indexing"
import {
    createMultifileUploadSlice,
    singleFileProgressStream,
    speedStream,
    UploadState,
} from "@nova/slice-multifile-upload"
import { createIdFromFile } from "@nova/util-strings"
import { runTask } from "@nova/webworker-pool"
import { type ImageMetadata, processImage } from "integrations/sensortool/processImage"
import { withRoot } from "@nova/with-root"
import { createSelector, createSlice } from "@reduxjs/toolkit"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { addFlightUsage } from "features/data-management/workspace/subscriptions/module"
import { selectIconById } from "features/entity-caching/icons/selectors"
import { addUploadTimestamp } from "features/map/layers/slice"
import { createIconImage, deleteIconImage } from "features/pubsub/entityActions/images/icon"
import { RootState } from "integrations/redux"
import store from "integrations/redux/store"
import { memoize } from "nodash"
import { createSpeedCallback } from "themes/default-tailwind/panels/GlobalUploadContainer"

type IconImageMeta = {
    organizationID: string
    fireID: string
    flightID: string // TODO: Change to entityId
    setID: string
    file: File
    index: string
}

export type IconImageState = Partial<IconImage> & {
    state: ImageProcessingState
    id: string
    warnings?: ImageWarnings
    deletedAt?: string
    tags?: Partial<PreviouslyParsedTagValues>
    flightId?: string // TODO: Change to entityId
    fileName?: string
    isCancelled?: boolean
    hasThumbnail?: boolean
}

const {
    actions,
    reducer: imagesReducer,
    selectors: {
        selectAllSets,
        selectAllUploads,
        selectSet,
        selectUpload,
        selectUploadsForSet,
        selectUploadIdsForSet,
        selectCancelledUploadIdsForSet,
        selectUploadsWithStates,
        selectUploadsForSetSummary: baseSelectUploadsForSetSummary,
        selectPendingForSet: baseSelectPendingForSet,
    },
} = createMultifileUploadSlice<"icon-images-v1", IconImageState, IconImageMeta>(
    "icon-images-v1",
    (m) =>
        ({
            id: createIdFromFile(m.file),
            state: ImageProcessingState.Pending,
        }) satisfies IconImageState,
    {
        addUploadToProject: (setId, upload) => {
            delete upload.tags
            delete upload.flightId
            return createIconImage({
                data: upload as IconImage,
                context: { flightId: setId },
            })
        },
    },
    async (state, meta, update, { useSignal, useSemaphore }) => {
        const deleteSignal = useSignal("delete", state.id)
        const cancelSignal = useSignal("cancel", meta.flightID)
        deleteSignal.then(() => {
            if (state.state !== ImageProcessingState.UploadDone)
                state = { ...state, deletedAt: new Date().toISOString() }
        })
        cancelSignal.then(() => {
            if (state.state !== ImageProcessingState.UploadDone) {
                state = {
                    ...state,
                    isCancelled: true,
                }
            }
        })
        let metadata: ImageMetadata
        // semaphore lock - maximum 15 pending to make sure we don't overload the machine, RGB images take a bit to chunk through
        await useSemaphore(8, async () => {
            update({ ...state, state: ImageProcessingState.Processing })
            if (state.deletedAt || state.isCancelled) return
            // process the data
            const fileBuffer = await meta.file.arrayBuffer()
            try {
                metadata = await runTask(processImage, { fileBuffer }, { transfer: [fileBuffer] })
            } catch (e) {
                console.warn("processImage failed, uploading without metadata", e)
            }

            state = {
                ...state,
                ...metadata?.gps,
                flightId: meta.setID,
                takenAt: dateAsNumber(metadata?.takenAt),
                state: ImageProcessingState.ProcessingDone,
                tags: metadata?.tags,
                make: metadata?.make,
                focalLength: metadata?.focalLength,
                size: meta.file.size,
                imageHeight: metadata?.imageHeight,
                imageWidth: metadata?.imageWidth,
                fileName: meta.file.name,
                index: meta.index,
            }
            update(state)
        })

        if (state.deletedAt || state.isCancelled) return
        store.dispatch(
            addUploadTimestamp({
                flightId: meta.setID,
                timestamp: new Date().toISOString(),
            })
        )
        state = {
            ...state,
            state: ImageProcessingState.PendingUpload,
        }
        update(state)

        // semaphore lock - revisit https://en.wikipedia.org/wiki/Semaphore_(programming) before poking around it this
        // 5 is the number of uploads that are allowed to be in progress at once. As soon as one finishes, it'll start the next
        await useSemaphore(5, async () => {
            if (state.deletedAt || state.isCancelled) return
            update({ ...state, state: ImageProcessingState.Uploading })

            try {
                const uploadId = state.id
                const unsub = store.subscribe(() => {
                    const icon = selectIconById(meta.flightID)(store.getState())
                    if (icon?.images.some((img) => img.id === uploadId)) {
                        unsub()
                        store.dispatch(actions.deleteUploadSync({ uploadId, setId: meta.setID }))
                    }
                })
                await fileManagementApi.putFile(meta.file, {
                    filename: state.id,
                    tags: {
                        dataType: IngestDataType.IconImage,
                        iconId: meta.flightID,
                        index: meta.index,
                        orgId: meta.organizationID,
                        projectId: meta.fireID,
                        fileName: meta.file.name,
                    },
                    onProgress: createSpeedCallback(({ speedMbps, percentUploaded }) => {
                        speedStream.setSpeed(meta.setID, state.id, speedMbps)
                        singleFileProgressStream.setProgress(state.id, percentUploaded / 100)
                    }),
                })
                if (state.deletedAt || state.isCancelled) return
                update({ ...state, state: ImageProcessingState.UploadDone })
            } catch (e) {
                throw e
            }
        })
        return { ...state }
    }
)

const stageFiles = (
    organizationID: string,
    fireID: string,
    iconId: string,
    files: File[],
    onFinish?: () => void
) => {
    const state = store.getState()
    const icon = selectIconById(iconId)(state)
    const allowedUploadCount = maxIconImagesCount - icon.images.length
    const meta = new Array<IconImageMeta>(
        allowedUploadCount > files.length ? files.length : allowedUploadCount
    )
    let totalSize = 0
    const lastImageOnIcon = [...icon.images]
        .sort((a, b) => compareFractionalIndices(a.index, b.index))
        .at(-1)
    let lastIndex = lastImageOnIcon?.index || START
    for (let i = 0; i < files.length && i < allowedUploadCount; i++) {
        lastIndex = createFractionalIndexBetween(lastIndex, END)
        meta[i] = {
            organizationID,
            fireID,
            flightID: iconId,
            setID: iconId,
            file: files[i],
            index: lastIndex,
        }
        totalSize += files[i].size
    }
    addFlightUsage(iconId, totalSize)
    if (meta.length !== files.length) {
        store.dispatch(markIconImageUploadAsExceededMaxUploads({ iconId }))
    } else {
        store.dispatch(markIconImageUploadAsNotExceededMaxUploads({ iconId }))
    }
    store.dispatch(showUploadPageForIcon({ id: iconId }))
    store.dispatch(markIconImageUploadAsNotCancelled({ iconId }))
    return actions.stageFiles({
        meta,
        onFinish,
    })
}

const uploadIconImages = (id: string) => actions.signal("upload", id)
const cancelUpload = (id: string) => {
    store.dispatch(markIconImageUploadAsCancelled({ iconId: id }))
    return actions.signal("cancel", id)
}
const deleteUpload = actions.deleteUpload
const deleteUploadSet = actions.deleteUploadSet

// this is summarizing the state of uploads for a set
// it's useful for determining if a set is uploading, how much of it is uploading, etc.
const selectUploadsForSetSummary = (id: string) =>
    baseSelectUploadsForSetSummary(
        id,
        (t) => t.state,
        (t) => t.state === ImageProcessingState.UploadDone
    )

const selectPendingUploadsForSet = (id: string) => baseSelectPendingForSet(id, "upload")

export const selectImageAndUploadIdsForIcon = memoize((iconId: string) =>
    createSelector([selectIconById(iconId), selectUploadsForSet(iconId)], (icon, uploads) => {
        return [
            ...icon.images.map((image) => ({ id: image.id, index: image.index, type: "Uploaded" })),
            ...(uploads
                ? uploads
                      .filter(
                          (upload): upload is UploadState<IconImageState> & { index: string } =>
                              "index" in upload
                      )
                      .map((upload) => ({
                          id: upload.id,
                          index: upload.index,
                          type: "ToUpload",
                      }))
                : []),
        ].sort((a, b) => compareFractionalIndices(a.index, b.index)) as {
            id: string
            index: string
            type: "Uploaded" | "ToUpload"
        }[]
    })
)
export const selectIconImageOrUploadById = memoize(
    (iconId: string, imageId: string) =>
        createSelector([selectIconById(iconId), selectUploadsForSet(iconId)], (icon, uploads) => {
            const foundImage = icon.images.find((image) => image.id === imageId)
            if (foundImage) return foundImage
            const foundUpload = uploads?.find((upload) => upload.id === imageId) as IconImageState
            return foundUpload
        }),
    (iconId, imageId) => `${iconId}|${imageId}`
)
export const selectUploadById = memoize(
    (iconId: string, imageId: string) =>
        createSelector([selectUploadsForSet(iconId)], (uploads) =>
            uploads?.find((upload) => upload.id === imageId)
        ),
    (iconId, imageId) => `${iconId}|${imageId}`
)

const selectIconMockJob = memoize((id: string) =>
    createSelector([selectIconById(id)], (icon) => {
        if (icon?.images.length > 0) return { status: JobStatusType.COMPLETED }
    })
)

type MockIconJob = {
    id: string
    mock: true
    upload: boolean
    name: string
    status: JobStatusType
}

type UIState = {
    mockJobs: Record<string, MockIconJob | undefined>
    selectedUpload: Record<string, boolean>
    leftUploadPageForIcon: Record<string, boolean>
    activePreviewIndex: number
    selectedImageIds: Record<string, Record<string, boolean>>
    uploadIsCancelled: Record<string, boolean>
    uploadHasExceededMax: Record<string, boolean>
}

const uiStateName = "iconImages-ui-v1"
type uiStateNameType = typeof uiStateName
const initialUIState: UIState = {
    mockJobs: {},
    selectedUpload: {},
    leftUploadPageForIcon: {},
    activePreviewIndex: 0,
    selectedImageIds: {},
    uploadIsCancelled: {},
    uploadHasExceededMax: {},
}

const {
    reducer: uiStateReducer,
    actions: {
        createMockIconJob,
        setSelectedUpload,
        leaveUploadPageForIcon,
        showUploadPageForIcon,
        cyclePreviewIndexLeft,
        cyclePreviewIndexRight,
        setPreviewIndex,
        selectIconImage,
        deselectIconImage,
        markIconImageUploadAsCancelled,
        markIconImageUploadAsNotCancelled,
        markIconImageUploadAsExceededMaxUploads,
        markIconImageUploadAsNotExceededMaxUploads,
    },
} = createSlice({
    name: uiStateName,
    initialState: { ...initialUIState },
    reducers: {
        createMockIconJob: (state, { payload }: { payload: MockIconJob }) => {
            state.mockJobs[payload.id] = payload
        },
        setSelectedUpload: (state, action) => {
            state.selectedUpload = action.payload
        },
        leaveUploadPageForIcon: (state, action: { payload: { id: string } }) => {
            state.leftUploadPageForIcon[action.payload.id] = true
        },
        showUploadPageForIcon: (state, action: { payload: { id: string } }) => {
            state.leftUploadPageForIcon[action.payload.id] = false
        },
        cyclePreviewIndexLeft: (state, action: { payload: { maxIndex: number } }) => {
            if (state.activePreviewIndex === 0) {
                state.activePreviewIndex = action.payload.maxIndex
            } else {
                state.activePreviewIndex = state.activePreviewIndex - 1
            }
        },
        cyclePreviewIndexRight: (state, action: { payload: { maxIndex: number } }) => {
            if (state.activePreviewIndex === action.payload.maxIndex) {
                state.activePreviewIndex = 0
            } else {
                state.activePreviewIndex = state.activePreviewIndex + 1
            }
        },
        setPreviewIndex: (state, action: { payload: { index: number } }) => {
            state.activePreviewIndex = action.payload.index
        },
        selectIconImage: (state, action: { payload: { iconId: string; imageId: string } }) => {
            if (!state.selectedImageIds[action.payload.iconId]) {
                state.selectedImageIds[action.payload.iconId] = {}
            }
            state.selectedImageIds[action.payload.iconId][action.payload.imageId] = true
        },
        deselectIconImage: (state, action: { payload: { iconId: string; imageId: string } }) => {
            if (!state.selectedImageIds[action.payload.iconId]) {
                state.selectedImageIds[action.payload.iconId] = {}
            }
            state.selectedImageIds[action.payload.iconId][action.payload.imageId] = false
        },
        markIconImageUploadAsCancelled: (state, action: { payload: { iconId: string } }) => {
            state.uploadIsCancelled[action.payload.iconId] = true
        },
        markIconImageUploadAsNotCancelled: (state, action: { payload: { iconId: string } }) => {
            state.uploadIsCancelled[action.payload.iconId] = false
        },
        markIconImageUploadAsExceededMaxUploads: (
            state,
            action: { payload: { iconId: string } }
        ) => {
            state.uploadHasExceededMax[action.payload.iconId] = true
        },
        markIconImageUploadAsNotExceededMaxUploads: (
            state,
            action: { payload: { iconId: string } }
        ) => {
            state.uploadHasExceededMax[action.payload.iconId] = false
        },
    },
    extraReducers: (b) =>
        b.addCase(deleteIconImage, (state, action) => {
            if (state.selectedImageIds[action.payload.context.flightId]) {
                delete state.selectedImageIds[action.payload.context.flightId][action.payload.id]
            }
        }),
})

export const deleteSelectedIconImages = ({ iconId }: { iconId: string }) => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        const state = getState() as RootState
        const selectedIconImages = selectSelectedIconImages(iconId)(state)
        Object.entries(selectedIconImages).forEach(([id, isSelected]) => {
            if (isSelected) {
                dispatch(
                    deleteIconImage({
                        id,
                        context: {
                            flightId: iconId,
                        },
                    })
                )
            }
        })
    }
}

const {
    selectMockIconJobById,
    selectSelectedUpload,
    selectIconsThatLeftUploadPage,
    selectActivePreviewIndex,
    selectIsIconImageSelected,
    selectSelectedIconImages,
    selectIsUploadMarkedAsCancelled,
    selectHasUploadExceededMax,
} = withRoot((r: Record<uiStateNameType, UIState>) => r[uiStateName], {
    selectMockIconJobById: (id: string) => (s) => s.mockJobs[id],
    selectSelectedUpload: () => (s) => s.selectedUpload,
    selectIconsThatLeftUploadPage: () => (s) => s.leftUploadPageForIcon,
    selectActivePreviewIndex: () => (s) => s.activePreviewIndex,
    selectIsIconImageSelected:
        ({ iconId, imageId }: { iconId: string; imageId: string }) =>
        (s) =>
            !!s.selectedImageIds[iconId]?.[imageId],
    selectSelectedIconImages: (id: string) => (s) => s.selectedImageIds[id] || {},
    selectIsUploadMarkedAsCancelled: (id: string) => (s) => !!s.uploadIsCancelled[id],
    selectHasUploadExceededMax: (id: string) => (s) => !!s.uploadHasExceededMax[id],
})

// export selectors
export {
    selectAllUploads,
    selectAllSets,
    selectSet,
    selectUpload,
    selectUploadsForSet,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
    selectUploadsWithStates,
    selectPendingUploadsForSet,
    selectUploadsForSetSummary,
    selectIconMockJob,
    selectMockIconJobById,
    selectSelectedUpload,
    selectIconsThatLeftUploadPage,
    selectActivePreviewIndex,
    selectIsIconImageSelected,
    selectSelectedIconImages,
    selectIsUploadMarkedAsCancelled,
    selectHasUploadExceededMax,
}

export {
    uploadIconImages,
    cancelUpload,
    deleteUpload,
    stageFiles,
    deleteUploadSet,
    createMockIconJob,
    setSelectedUpload,
    leaveUploadPageForIcon,
    showUploadPageForIcon,
    cyclePreviewIndexLeft,
    cyclePreviewIndexRight,
    setPreviewIndex,
    selectIconImage,
    deselectIconImage,
    markIconImageUploadAsCancelled,
    markIconImageUploadAsNotCancelled,
    markIconImageUploadAsExceededMaxUploads,
    markIconImageUploadAsNotExceededMaxUploads,
}

// export reducer
export const reducer = {
    ...imagesReducer,
}

export const uiReducer = { [uiStateName]: uiStateReducer }
